import { useCallback, useEffect, useMemo, useState } from "react";
import FilterMenu from "../../../components/FilterMenu";
import { ChartAreaLoader } from "../../../components/Loading";
import BlockRangeSelector from "../../../components/BlockRangeSelector/BlockRangeSelector";
import { ReMixChart } from "../../../components/recharts/ReMixCharts";
import {
  getRajasthanDemandMetrics,
  getDiscomDrawalScheduleMetrics,
  getTotalDrawalScheduleMetrics,
} from "../Utility/RestApi";
import { H_CHART_TYPES } from "../../Hppc/const";
import LegendSummaryTable, {
  ChartTable,
  downloadChartTableCSV,
  downloadSummaryCSV,
} from "../../../components/table/SummaryTable";
import NewFilterContainer from "../../../components/NewFilterContainer ";
import MainPageContainer from "../../../components/MainPageContainer";
import SecondaryHeader from "./SecondaryHeader";
import Tooltip from "../../../components/tooltip";
import { useDispatch, useSelector } from "react-redux";
import { getTimeRange } from "../../Dashboard/Exchange3/Chart";
import { AddDatesWithFileName } from "../../../helpers/helper";
import { formatDateForAPI } from "../Utility/const";
import { setLoadingFilterName } from "../../../store/state/FilterLoadingState";
import { lightenColor } from "../../../components/recharts/ReMixCharts";
import { stringToConsistentColor } from "../../../extensions/string";
import { ExchangeColors } from "../../Dashboard/Exchange3/FormatData";
import { useLegendFilter } from "../../../hooks/useLegendFilter";
import MetricChip from "../../../components/Metrics";
import { DiscomLoadMetrics } from "../../../models/metrics";
import { ReactComponent as PeakIcon } from "../../../icons/peakicon.svg";
import { ReactComponent as AvgIcon } from "../../../icons/AvgIcon.svg";
import { ReactComponent as MinIcon } from "../../../icons/MinIcon.svg";
import { ReactComponent as TotalIcon } from "../../../icons/TotalIcon.svg";
const X_AXIS_MODES = [
  { label: "Intervals", value: "intervals" },
  { label: "Peak Analysis", value: "peak" },
  { label: "Summary", value: "summary" },
];

const UNITS = ["MW", "MWh", "LU"];

const GROUP_BY = ["Discom Load", "Total Discom Load", "RVPN Drawal"];

const DISCOM = [
  { label: "JDVVNL", key: "JDVVNL" },
  { label: "JVVNL", key: "JVVNL" },
  { label: "AVVNL", key: "AVVNL" },
];

const TYPE = ["Schedule", "Drawal"];

interface RajasthanSummaryAnalysisProps {
  onXAxisModeChange: (item: string, index: number) => void;
  selectedUnit: string;
  onUnitChange: (unit: string) => void;
  selectedDiscom: string[];
  onDiscomChange: (items: string[]) => void;
  selectedGroupKeys: string;
  onGroupByChange: (index: number) => void;
  selectedSuffix: any[];
  onTypeChange: (selected: string[]) => void;
  chartSuffixes: any[];
  metrics: DiscomLoadMetrics | null;
}

export function RajasthanSummaryAnalysis({
  onXAxisModeChange,
  selectedUnit,
  onUnitChange,
  selectedDiscom,
  onDiscomChange,
  selectedGroupKeys,
  onGroupByChange,
  selectedSuffix,
  onTypeChange,
  chartSuffixes,
  metrics,
}: RajasthanSummaryAnalysisProps) {
  const dataTab = useSelector((state: any) => state.viewMode.activeView);
  const activeTab = useSelector((state: any) => state.switchTabs.activeTab);
  const dispatch = useDispatch();
  const DefaultDates = useSelector(
    (state: any) => state.defaultDatesSetter.defaultDates,
  );

  const [summaryData, setSummaryData] = useState<any[]>([]);
  const [discomData, setDiscomData] = useState<any[]>([]);
  const [totalDiscomData, setTotalDiscomData] = useState<any[]>([]);
  const [chartData, setChartData] = useState<any[]>([]);
  const [tableData, setTableData] = useState<any[]>([]);
  const [chartKeys, setChartKeys] = useState<any[]>([]);
  const [selectedChartType, setSelectedChartType] = useState(H_CHART_TYPES[1]);
  const [selectedBlock, setSelectedBlock] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchSummaryData = useCallback(async () => {
    setLoading(true);

    try {
      const dates = DefaultDates[activeTab]?.defaultRangeDate;
      if (!dates || !dates.startDate || !dates.endDate) {
        console.error("Invalid date range");
        setLoading(false);
        return;
      }

      const startDateStr = formatDateForAPI(dates.startDate);
      const endDateStr = formatDateForAPI(dates.endDate);

      const [rvpnData, discomMetrics, totalDiscomMetrics] = await Promise.all([
        getRajasthanDemandMetrics(startDateStr, endDateStr),
        getDiscomDrawalScheduleMetrics(startDateStr, endDateStr),
        getTotalDrawalScheduleMetrics(startDateStr, endDateStr),
      ]);

      setSummaryData(Array.isArray(rvpnData) ? rvpnData : []);
      setDiscomData(Array.isArray(discomMetrics) ? discomMetrics : []);
      setTotalDiscomData(
        Array.isArray(totalDiscomMetrics) ? totalDiscomMetrics : [],
      );
    } catch (error) {
      console.error("Error fetching summary data:", error);
      setSummaryData([]);
      setDiscomData([]);
      setTotalDiscomData([]);
    } finally {
      setLoading(false);
    }
  }, [DefaultDates, activeTab]);

  useEffect(() => {
    fetchSummaryData();
  }, [fetchSummaryData]);

  useEffect(() => {
    const conversionFactor =
      selectedUnit === "MWh" ? 0.25 : selectedUnit === "LU" ? 0.0025 : 1;

    if (selectedGroupKeys === "RVPN Drawal") {
      const processedData = summaryData.map((item) => ({
        slot: item.slot,
        slot_number: item.slot,
        slot_with_time:
          item.slot && item.time_string
            ? `${item.slot} (${item.time_string})`
            : item.slot,
        min_demand: item.min_demand * conversionFactor,
        max_demand: item.max_demand * conversionFactor,
        avg_demand: item.avg_demand * conversionFactor,
        mode_demand_window_formula:
          item.mode_demand_window_formula * conversionFactor,
        min_demand_date: item.date_of_min,
        max_demand_date: item.date_of_max,
      }));

      let finalData = processedData;
      if (selectedBlock.length > 0) {
        finalData = finalData.filter((item) =>
          selectedBlock.map((b) => b.value).includes(item.slot),
        );
      }
      setChartData(finalData);
      setTableData(finalData);
      setChartKeys([]);
    } else if (selectedGroupKeys === "Total Discom Load") {
      const processedData = totalDiscomData.map((item) => ({
        slot: item.slot,
        slot_number: item.slot,
        slot_with_time:
          item.slot && item.time_string
            ? `${item.slot} (${item.time_string})`
            : item.slot,

        min_drawal: (item.min_drawal || 0) * conversionFactor,
        max_drawal: (item.max_drawal || 0) * conversionFactor,
        avg_drawal: (item.avg_drawal || 0) * conversionFactor,
        mode_drawal_window_formula:
          (item.mode_drawal_window_formula || 0) * conversionFactor,

        min_schedule: (item.min_schedule || 0) * conversionFactor,
        max_schedule: (item.max_schedule || 0) * conversionFactor,
        avg_schedule: (item.avg_schedule || 0) * conversionFactor,
        mode_schedule_window_formula:
          (item.mode_schedule_window_formula || 0) * conversionFactor,

        min_drawal_date: item.date_of_min_drawal,
        max_drawal_date: item.date_of_max_drawal,
        min_schedule_date: item.date_of_min_schedule,
        max_schedule_date: item.date_of_max_schedule,
      }));

      processedData.sort((a, b) => a.slot - b.slot);

      let finalData = processedData;
      if (selectedBlock.length > 0) {
        finalData = finalData.filter((item) =>
          selectedBlock.map((b) => b.value).includes(item.slot),
        );
      }
      setChartData(finalData);
      setTableData(finalData);
      setChartKeys([]);
    } else {
      let filteredDiscomData = discomData;

      if (selectedDiscom.length > 0 && selectedDiscom.length < DISCOM.length) {
        filteredDiscomData = filteredDiscomData.filter((item) =>
          selectedDiscom.includes(item.discom),
        );
      }

      const slotMap = new Map<number, any>();

      filteredDiscomData.forEach((item) => {
        const slot = item.slot;
        const discom = item.discom;

        if (!slotMap.has(slot)) {
          slotMap.set(slot, {
            slot: slot,
            slot_number: slot,
            slot_with_time:
              item.slot && item.time_string
                ? `${item.slot} (${item.time_string})`
                : item.slot,
          });
        }

        const slotData = slotMap.get(slot);

        slotData[`${discom}_min_drawal`] = item.min_drawal * conversionFactor;
        slotData[`${discom}_max_drawal`] = item.max_drawal * conversionFactor;
        slotData[`${discom}_avg_drawal`] = item.avg_drawal * conversionFactor;
        slotData[`${discom}_mode_drawal_window_formula`] =
          item.mode_drawal_window_formula * conversionFactor;

        slotData[`${discom}_min_schedule`] =
          item.min_schedule * conversionFactor;
        slotData[`${discom}_max_schedule`] =
          item.max_schedule * conversionFactor;
        slotData[`${discom}_avg_schedule`] =
          item.avg_schedule * conversionFactor;
        slotData[`${discom}_mode_schedule_window_formula`] =
          item.mode_schedule_window_formula * conversionFactor;

        slotData[`${discom}_min_drawal_date`] = item.date_of_min_drawal;
        slotData[`${discom}_max_drawal_date`] = item.date_of_max_drawal;
        slotData[`${discom}_min_schedule_date`] = item.date_of_min_schedule;
        slotData[`${discom}_max_schedule_date`] = item.date_of_max_schedule;
      });

      const processedData = Array.from(slotMap.values());

      processedData.sort((a, b) => a.slot - b.slot);

      let finalData = processedData;
      if (selectedBlock.length > 0) {
        finalData = finalData.filter((item) =>
          selectedBlock.map((b) => b.value).includes(item.slot),
        );
      }
      setChartData(finalData);
      setTableData(finalData);

      const uniqueDiscoms = Array.from(
        new Set(filteredDiscomData.map((item) => item.discom)),
      ).sort();
      setChartKeys(uniqueDiscoms);
    }
  }, [
    summaryData,
    discomData,
    totalDiscomData,
    selectedUnit,
    selectedGroupKeys,
    selectedDiscom,
    selectedBlock,
  ]);

  const legends = useMemo(() => {
    if (selectedGroupKeys === "RVPN Drawal") {
      return [
        {
          dataKey: "min_demand",
          name: "Min Demand",
          type: selectedChartType.value,
          stackId:
            selectedChartType.stackId !== undefined ? "summary" : undefined,
          dateKey: "min_demand_date",
        },
        {
          dataKey: "max_demand",
          name: "Max Demand",
          type: selectedChartType.value,
          stackId:
            selectedChartType.stackId !== undefined ? "summary" : undefined,
          dateKey: "max_demand_date",
        },
        {
          dataKey: "avg_demand",
          name: "Average Demand",
          type: selectedChartType.value,
          stackId:
            selectedChartType.stackId !== undefined ? "summary" : undefined,
        },
        {
          dataKey: "mode_demand_window_formula",
          name: "Mode Demand",
          type: selectedChartType.value,
          stackId:
            selectedChartType.stackId !== undefined ? "summary" : undefined,
        },
      ];
    } else if (selectedGroupKeys === "Total Discom Load") {
      const legends: any[] = [];

      selectedSuffix.forEach(({ suffix, displayName, stackSuffix }) => {
        if (suffix === "schedule") {
          legends.push(
            {
              dataKey: "min_schedule",
              name: "Min Schedule",
              type: selectedChartType.value,
              stackId:
                selectedChartType.stackId !== undefined
                  ? stackSuffix
                  : undefined,
              dateKey: "min_schedule_date",
            },
            {
              dataKey: "max_schedule",
              name: "Max Schedule",
              type: selectedChartType.value,
              stackId:
                selectedChartType.stackId !== undefined
                  ? stackSuffix
                  : undefined,
              dateKey: "max_schedule_date",
            },
            {
              dataKey: "avg_schedule",
              name: "Average Schedule",
              type: selectedChartType.value,
              stackId:
                selectedChartType.stackId !== undefined
                  ? stackSuffix
                  : undefined,
            },
            {
              dataKey: "mode_schedule_window_formula",
              name: "Mode Schedule",
              type: selectedChartType.value,
              stackId:
                selectedChartType.stackId !== undefined
                  ? stackSuffix
                  : undefined,
            },
          );
        }

        if (suffix === "drawal") {
          legends.push(
            {
              dataKey: "min_drawal",
              name: "Min Drawal",
              type: selectedChartType.value,
              stackId:
                selectedChartType.stackId !== undefined
                  ? stackSuffix
                  : undefined,
              dateKey: "min_drawal_date",
            },
            {
              dataKey: "max_drawal",
              name: "Max Drawal",
              type: selectedChartType.value,
              stackId:
                selectedChartType.stackId !== undefined
                  ? stackSuffix
                  : undefined,
              dateKey: "max_drawal_date",
            },
            {
              dataKey: "avg_drawal",
              name: "Average Drawal",
              type: selectedChartType.value,
              stackId:
                selectedChartType.stackId !== undefined
                  ? stackSuffix
                  : undefined,
            },
            {
              dataKey: "mode_drawal_window_formula",
              name: "Mode Drawal",
              type: selectedChartType.value,
              stackId:
                selectedChartType.stackId !== undefined
                  ? stackSuffix
                  : undefined,
            },
          );
        }
      });

      return legends;
    } else {
      const legends: any[] = [];
      const metricTypes = [
        { field: "min", displayName: "Min", hasDate: true },
        { field: "max", displayName: "Max", hasDate: true },
        { field: "avg", displayName: "Average", hasDate: false },
        { field: "mode", displayName: "Mode", hasDate: false },
      ];

      chartKeys.forEach((discom, discomIndex) => {
        selectedSuffix.forEach(({ suffix, displayName, stackSuffix }) => {
          metricTypes.forEach((metric) => {
            const fieldSuffix =
              metric.field === "mode"
                ? `mode_${suffix}_window_formula`
                : `${metric.field}_${suffix}`;
            const dataKey = `${discom}_${fieldSuffix}`;
            const legendName = `${discom} ${metric.displayName} ${displayName}`;

            const dateKey = metric.hasDate
              ? `${discom}_${metric.field}_${suffix}_date`
              : undefined;

            legends.push({
              dataKey: dataKey,
              name: legendName,
              legendColor: undefined,
              stroke: undefined,
              type: selectedChartType.value,
              stackId:
                selectedChartType.stackId !== undefined
                  ? stackSuffix
                  : undefined,
              dateKey: dateKey,
            });
          });
        });
      });

      return legends;
    }
  }, [selectedGroupKeys, selectedChartType, selectedSuffix, chartKeys]);

  const { visibleLegends, handleLegendSelect, selectedLegends } =
    useLegendFilter(legends);

  const customTooltipFormatter = useCallback(
    (params: any) => {
      const param = Array.isArray(params) ? params[0] : params;
      let items = Array.isArray(params) ? params : [params];

      let title = param.name;
      const isTimeSlot = true;
      if (isTimeSlot) {
        title = getTimeRange(parseInt(param.name)) + ` (${param.name})`;
      }

      const dataPoint = chartData[param.dataIndex];

      const sortedItems = [...items].sort((a, b) => {
        const nameA = (a.seriesName || "").toLowerCase();
        const nameB = (b.seriesName || "").toLowerCase();
        return nameA.localeCompare(nameB);
      });

      const tooltipItems = sortedItems.map((item) => {
        const value = parseFloat(item.value)
          .toFixed(2)
          .replace(/\B(?=(\d{3})+(?!\d))/g, ",");
        const bgColor = item.color;

        let displaySeriesName = item.seriesName;

        if (selectedGroupKeys === "RVPN Drawal") {
          if (item.seriesName === "Min Demand" && dataPoint?.min_demand_date) {
            displaySeriesName = `Min Demand (${dataPoint.min_demand_date})`;
          } else if (
            item.seriesName === "Max Demand" &&
            dataPoint?.max_demand_date
          ) {
            displaySeriesName = `Max Demand (${dataPoint.max_demand_date})`;
          }
        } else if (selectedGroupKeys === "Total Discom Load") {
          if (
            item.seriesName === "Min Schedule" &&
            dataPoint?.min_schedule_date
          ) {
            displaySeriesName = `Min Schedule (${dataPoint.min_schedule_date})`;
          } else if (
            item.seriesName === "Max Schedule" &&
            dataPoint?.max_schedule_date
          ) {
            displaySeriesName = `Max Schedule (${dataPoint.max_schedule_date})`;
          } else if (
            item.seriesName === "Min Drawal" &&
            dataPoint?.min_drawal_date
          ) {
            displaySeriesName = `Min Drawal (${dataPoint.min_drawal_date})`;
          } else if (
            item.seriesName === "Max Drawal" &&
            dataPoint?.max_drawal_date
          ) {
            displaySeriesName = `Max Drawal (${dataPoint.max_drawal_date})`;
          }
        } else if (selectedGroupKeys === "Discom Load") {
          const seriesNameParts = item.seriesName.split(" ");
          const discom = seriesNameParts[0];

          if (item.seriesName.includes("Min Schedule")) {
            const dateField = `${discom}_min_schedule_date`;
            if (dataPoint?.[dateField]) {
              displaySeriesName = `${item.seriesName} (${dataPoint[dateField]})`;
            }
          } else if (item.seriesName.includes("Max Schedule")) {
            const dateField = `${discom}_max_schedule_date`;
            if (dataPoint?.[dateField]) {
              displaySeriesName = `${item.seriesName} (${dataPoint[dateField]})`;
            }
          } else if (item.seriesName.includes("Min Drawal")) {
            const dateField = `${discom}_min_drawal_date`;
            if (dataPoint?.[dateField]) {
              displaySeriesName = `${item.seriesName} (${dataPoint[dateField]})`;
            }
          } else if (item.seriesName.includes("Max Drawal")) {
            const dateField = `${discom}_max_drawal_date`;
            if (dataPoint?.[dateField]) {
              displaySeriesName = `${item.seriesName} (${dataPoint[dateField]})`;
            }
          }
        }

        return `
        <div class="tooltip-item" data-name="${item.seriesName?.toLowerCase()}">
          <div class="color-indicator" style="background-color: ${bgColor}"></div>
          <div class="series-name">${displaySeriesName}</div>
          <div class="value">${value} ${selectedUnit}</div>
        </div>
      `;
      });

      const tooltipId = `tooltip-${Date.now()}`;

      return `
      <div class="enhanced-tooltip" id="${tooltipId}">
        <div class="tooltip-header">
          <div class="tooltip-title">${title}</div>
        </div>
        
        <div class="tooltip-content">
          ${tooltipItems.join("")}
        </div>
      </div>
      
      <style>
        .enhanced-tooltip {
          font-family: 'Inter', system-ui, sans-serif;
          width: 100%;
          min-width: 200px;
        }
        .tooltip-header {
          display: flex;
          flex-direction: column;
          gap: 8px;
          justify-content: start;
          align-items: start;
          border-bottom: 1px solid #eaeaea;
          padding-bottom: 8px;
          margin-bottom: 8px;
        }
        .tooltip-title {
          font-weight: 600;
          font-size: 13px;
          color: #333;
        }
        .tooltip-content {
          max-height: 300px;
          overflow-y: auto;
          padding-right: 10px;
        }
        .tooltip-item {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 4px 0;
          border-bottom: 1px solid #f0f0f0;
        }
        .tooltip-item:last-child {
          border-bottom: none;
        }
        .color-indicator {
          width: 10px;
          height: 10px;
          flex-shrink: 0;
          border-radius: 50%;
        }
        .series-name {
          font-weight: 500;
          font-size: 12px;
          color: #333;
          flex: 1;
        }
        .value {
          font-weight: 600;
          font-size: 12px;
          color: #333;
        }
      </style>
    `;
    },
    [chartData, selectedUnit, selectedGroupKeys],
  );

  const getExtraKeys = () => {
    return ["slot_number", "Date"];
  };

  const getExtraLabels = () => {
    return ["Block", "Date"];
  };

  const tablePar = {
    legends: visibleLegends,
    data: chartData,
    tableData: tableData,
    defaultSortKey: "block",
    itemsPerPage: 96,
    unit: selectedUnit,
    block_key: "slot_number",
    extraKeys: getExtraKeys(),
    extraLabels: getExtraLabels(),
    filename: AddDatesWithFileName({
      fileName: "Load Summary",
      dates: DefaultDates[activeTab].defaultRangeDate || [],
    }),
  };

  return (
    <div className="gas-page-container">
      <NewFilterContainer>
        <div className="side-filters-container text-nowrap">
          <FilterMenu
            title="X-Axis"
            items={X_AXIS_MODES.map((item) => item.label)}
            selectedItems={["Summary"]}
            onSelect={onXAxisModeChange}
          />

          <FilterMenu
            title="Unit"
            items={UNITS}
            selectedItems={[selectedUnit]}
            onSelect={(item, index) => {
              onUnitChange(item);
            }}
          />

          {selectedGroupKeys !== "RVPN Drawal" && (
            <FilterMenu
              title="Discom"
              items={DISCOM.map((item) => item.label)}
              selectedItems={selectedDiscom}
              onSelect={(item, index) => {}}
              setSelectedItems={(item) => {
                if (selectedGroupKeys === "Total Discom Load") {
                  dispatch(setLoadingFilterName(""));
                  return;
                }
                onDiscomChange(item);
              }}
            />
          )}

          <FilterMenu
            title="Group By"
            items={GROUP_BY}
            selectedItems={[selectedGroupKeys]}
            onSelect={(item, index) => {
              onGroupByChange(index);
            }}
          />

          {selectedGroupKeys !== "RVPN Drawal" && (
            <FilterMenu
              title="Type"
              items={TYPE}
              selectedItems={selectedSuffix.map(
                (item) => item?.displayName || "",
              )}
              setSelectedItems={(selected: string[]) => {
                onTypeChange(selected);
              }}
            />
          )}

          <FilterMenu
            title="Chart Type"
            items={H_CHART_TYPES.map((item) => item.label)}
            selectedItems={[selectedChartType.label]}
            onSelect={(item, index) => {
              setSelectedChartType(H_CHART_TYPES[index]);
            }}
          />
        </div>
      </NewFilterContainer>

      <MainPageContainer>
        <SecondaryHeader />
        {metrics && (
          <div className="flex flex-wrap gap-4 my-0 justify-center">
            <MetricChip
              icon={<PeakIcon width={20} height={19} />}
              items={[
                {
                  label: "Peak Load",
                  value: metrics["Peak Load (MW)"].value,
                  unit: metrics["Peak Load (MW)"].unit,
                },
              ]}
            />
            <MetricChip
              icon={<AvgIcon width={20} height={19} />}
              items={[
                {
                  label: "Average Load",
                  value: metrics["Average Load (MW)"].value,
                  unit: metrics["Average Load (MW)"].unit,
                },
              ]}
            />
            <MetricChip
              icon={<MinIcon width={20} height={19} />}
              items={[
                {
                  label: "Minimum Load",
                  value: metrics["Minimum Load (MW)"].value,
                  unit: metrics["Minimum Load (MW)"].unit,
                },
              ]}
            />
            <MetricChip
              icon={<TotalIcon width={20} height={19} />}
              items={[
                {
                  label: "Total Energy",
                  value: metrics["Total Energy (MU)"].value,
                  unit: metrics["Total Energy (MU)"].unit,
                },
              ]}
            />
          </div>
        )}
        <div className="gas-chart-container">
          {loading ? (
            <ChartAreaLoader />
          ) : (
            <>
              <div className="gas-heading gap-x-10">
                <h2 className="chartHeading w-80">
                  Load Summary ({selectedUnit})
                </h2>
                <div className="flex gap-x-6 justify-end">
                  <BlockRangeSelector
                    blocks={Array.from({ length: 96 }, (_, i) => i + 1)}
                    placeholder="Search Block"
                    selectedOptions={selectedBlock.map((item) => item.value)}
                    setSelectedOptions={setSelectedBlock}
                  />
                  <button
                    onClick={() => {
                      dataTab.label === "Chart" &&
                        downloadChartTableCSV({
                          ...tablePar,
                        });
                      dataTab.label === "Summary" &&
                        downloadSummaryCSV(tablePar);
                      dataTab.label === "Table" &&
                        downloadChartTableCSV(tablePar);
                    }}
                    className="downloadButton"
                  >
                    Download Data
                  </button>
                </div>
              </div>
              {dataTab.label === "Chart" && (
                <ReMixChart
                  key={`${selectedGroupKeys}-${selectedChartType.value}-${selectedUnit}`}
                  data={chartData}
                  showBrush={undefined}
                  isTimeSlot={false}
                  onlyTitle={true}
                  barCategoryGap={10}
                  unit={selectedUnit}
                  yAxisLabel={selectedUnit}
                  legends={legends}
                  onLegendSelect={handleLegendSelect}
                  defaultSelectedLegends={selectedLegends}
                  xDataKey="slot"
                  secondXDataKey={undefined}
                  customTooltip={customTooltipFormatter}
                />
              )}

              {dataTab.label === "Summary" && (
                <LegendSummaryTable {...tablePar} />
              )}
              {dataTab.label === "Table" && <ChartTable {...tablePar} />}
            </>
          )}
        </div>
        <div className="gna-footer-container">
          <div className="grid grid-cols-3 justify-between gap-5 ">
            <Tooltip
              note={
                <p>
                  DISCOM Energy Reports (RUVITL), Load Shedding Reports (RUVITL)
                </p>
              }
              text="1.	 Actual Drawal = Restricted Demand
           Total Load (Unrestricted Demand) = Actual Drawal + Load Shedding 
2.	 Load Shedding has been distributed across time blocks based on the actual duration of load shedding
3.	MW = Represents the average (MW) when the selected time interval is Date or Month.
"
            >
              <p className="gna-footer-text">Source</p>
            </Tooltip>
            <p className="gna-footer-text col-span-1 text-center">
              Powered by <strong>GNA Energy</strong>
            </p>
            <strong className="gna-footer-text col-span-1 text-end">
              Beta Version
            </strong>
          </div>
        </div>
      </MainPageContainer>
    </div>
  );
}
